import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';
const API_ROOT = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
const apiUrl = (path) => `${API_ROOT}/${path}`;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const normalizeUser = useCallback((responseData) => {
    const payload = responseData || {};
    const apiUser = payload.user || {};
    const company = apiUser.company || payload.company || null;

    const companyName =
      apiUser.company_name ||
      apiUser.compnay_name ||
      payload.company_name ||
      payload.compnay_name ||
      company?.company_name ||
      company?.name ||
      '';

    const companyId =
      apiUser.company_id ||
      apiUser.compnay_id ||
      payload.company_id ||
      payload.compnay_id ||
      company?.company_id ||
      company?.id ||
      '';

    return {
      ...apiUser,
      ...(company ? { company } : {}),
      ...(companyName ? { company_name: companyName } : {}),
      ...(companyId ? { company_id: companyId } : {})
    };
  }, []);

  const checkAuthStatus = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(apiUrl('check_token.php'), {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        setUser(normalizeUser(response.data));
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      localStorage.removeItem('authToken');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [normalizeUser]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(apiUrl('login.php'), {
        username,
        password
      });

      if (response.data.success) {
        localStorage.setItem('authToken', response.data.token);
        setIsAuthenticated(true);
        setUser(normalizeUser(response.data));
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data.error || 'Login failed'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed'
      };
    }
  };

  const register = async (username, password, email, companyData) => {
    try {
      // Create a flat structure that matches what PHP expects
      const registerData = {
        username,
        password,
        email,
        // Spread all company data fields to make them top-level
        ...companyData
      };

      console.log('Sending registration data:', registerData); // Debug log

      const response = await axios.post(apiUrl('register.php'), registerData);

      console.log('Registration response:', response.data); // Debug log

      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        return {
          success: false,
          error: response.data.error || 'Registration failed'
        };
      }
    } catch (error) {
      console.error('Registration error:', error); // Debug log
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed'
      };
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      try {
        await axios.post(apiUrl('logout.php'), {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
    setUser(null);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    register,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
